import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Callout } from '@/components/ui/callout'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useSetGhlNameFormat } from '@/hooks/useGoHighLevel'
import { NAME_FORMAT_PRESETS, validateNameFormat } from '@lib/helpers/event-name-format'
import type { GhlCompanySettingsDto } from '@lib/dto/gohighlevel.dto'
import { sampleName } from './nameSample'

interface GhlNameFormatEditorProps {
  settings: GhlCompanySettingsDto
}

/**
 * How the company names events created from a GoHighLevel lead, at every venue,
 * so a management company's venues name events alike. Suggested from the
 * company's existing events.
 *
 * Mounted with `key={settings.nameFormat}`, so a saved or reset format starts the
 * draft afresh rather than being copied in by an effect.
 */
export function GhlNameFormatEditor({ settings }: GhlNameFormatEditorProps) {
  const save = useSetGhlNameFormat()
  const [format, setFormat] = useState(settings.nameFormat)
  const [problem, setProblem] = useState<string | null>(null)

  const dirty = format.trim() !== settings.nameFormat || !settings.nameFormatIsSet
  const preview = validateNameFormat(format) === null ? sampleName(format) : null

  function submit(): void {
    const issue = validateNameFormat(format.trim())
    setProblem(issue)
    if (issue !== null) return
    save.mutate({ nameFormat: format.trim() }, { onError: (e) => setProblem(requestErrorMessage(e)) })
  }

  return (
    <div className="space-y-4">
      {!settings.nameFormatIsSet && settings.suggestedNameFormat !== settings.nameFormat ? (
        <Callout
          tone="neutral"
          title="Suggested from your events"
          action={
            <Button variant="outline" size="sm" onClick={() => setFormat(settings.suggestedNameFormat)}>
              Use it
            </Button>
          }
        >
          <code className="text-xs">{settings.suggestedNameFormat}</code>
        </Callout>
      ) : null}

      <Field label="Start from" htmlFor="ghl-name-preset">
        <Select
          id="ghl-name-preset"
          value=""
          onChange={(e) => {
            if (e.target.value !== '') setFormat(e.target.value)
          }}
        >
          <option value="">Choose a common format…</option>
          {NAME_FORMAT_PRESETS.map((p) => (
            <option key={p.format} value={p.format}>
              {sampleName(p.format)}
            </option>
          ))}
        </Select>
      </Field>

      <Field
        label="Format"
        htmlFor="ghl-name-format"
        error={problem}
        hint="Parts: {first} {last} {partnerFirst} {partnerLast} and {date:M.D.YY}. Date letters: M MM MMM MMMM, D DD, YY YYYY. Anything else is written as is; a part GoHighLevel doesn’t have is left out."
      >
        <Input
          id="ghl-name-format"
          value={format}
          onChange={(e) => {
            setFormat(e.target.value)
            setProblem(null)
          }}
          autoComplete="off"
        />
      </Field>

      <p className="m-0 text-sm text-ink-muted">
        Preview: {preview === null || preview === '' ? '—' : <span className="text-ink">{preview}</span>}
      </p>

      <div className="flex flex-wrap gap-2">
        <Button size="sm" disabled={!dirty || save.isPending} onClick={submit}>
          {save.isPending ? 'Saving…' : 'Save format'}
        </Button>
        {settings.nameFormatIsSet ? (
          <Button
            variant="ghost"
            size="sm"
            disabled={save.isPending}
            onClick={() => save.mutate({ nameFormat: null }, { onError: (e) => setProblem(requestErrorMessage(e)) })}
          >
            Reset to default
          </Button>
        ) : null}
      </div>
    </div>
  )
}
